import { Button, Classes } from '@blueprintjs/core';
import * as React from 'react';
import { isTesting } from 'vet-bones/utils';

import { PropertyTypeSelector } from 'src/components/PropertyTypeSelector';
import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
  PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { ProjectDiagramEditableIri } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableIri';
import { ProjectDiagramEditableTextArea } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableTextArea';
import { ProjectDiagramRelationshipDrawerTable } from 'src/containers/ProjectDiagram/components/relationshipDrawer/ProjectDiagramRelationshipDrawerTable';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useAddModelToIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useAddModelToIri';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { useCreateModelConceptClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import {
  useAddClassForRelationship,
  useRemoveClassForRelationship,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptClassForRelationship';
import { useModelSelectItems } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelSelectItems';
import { useUpdateDescriptionForIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateDescriptionForIri';
import { useUpdateIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateIri';
import { useUpdateLabel } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateLabel';
import { useUpdatePropertyType } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdatePropertyType';
import { useRemoveModelConceptRelationship } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelConceptRelationship';
import { useRemoveModelForIri } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelForIri';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { relationshipDraftChangesSelector } from 'src/selectors/draftRelationship';
import { entityDrawerLinkDataSelector } from 'src/selectors/entityDrawerLinkData';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectDescriptionsByIriSelector } from 'src/selectors/projectDescriptionsByIri';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectModelItemsSelector } from 'src/selectors/projectModelItems';
import { projectRelationshipDomainAndRangesSelector } from 'src/selectors/projectRelationshipDomainAndRanges';
import { propertyTypeByIriSelector } from 'src/selectors/propertyType';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramRelationshipDrawer: React.VFC = () => {
  const { projectId } = useRouterParams();

  // entityDrawerLinkDataSelector is generic with a type-guard arg, so it can't
  // be memoized at the source; memoize the instance here instead.
  const linkDataSelector = React.useMemo(
    () => entityDrawerLinkDataSelector(projectId, isModelConceptRelationship),
    [projectId]
  );
  const linkData = useReduxSelector(linkDataSelector);

  const relationshipIri = linkData?.value || '';

  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));

  const descriptionsByIri = useReduxSelector(
    projectDescriptionsByIriSelector(projectId)
  );

  const { isCustomPropertyType, propertyType } = useReduxSelector(
    propertyTypeByIriSelector(projectId, relationshipIri)
  );

  const { domains, ranges } = useReduxSelector(
    projectRelationshipDomainAndRangesSelector(projectId, relationshipIri || '')
  );

  const allClassItems = useReduxSelector(projectClassItemsSelector(projectId));

  const allModelItems = useReduxSelector(projectModelItemsSelector(projectId));

  const { isDescriptionModified, isLabelModified, addedSources, addedTargets } =
    useReduxSelector(
      relationshipDraftChangesSelector(projectId, relationshipIri)
    );

  const { availableModels, currentModels } =
    useModelSelectItems(relationshipIri);

  const domainAndRangeSelectItems = React.useMemo(() => {
    return {
      currentDomains: allClassItems.filter((item) => domains.has(item.value)),
      availableDomains: allClassItems.filter(
        (item) => !domains.has(item.value)
      ),
      currentRanges: allClassItems.filter((item) => ranges.has(item.value)),
      availableRanges: allClassItems.filter((item) => !ranges.has(item.value)),
    };
  }, [allClassItems, domains, ranges]);

  const handleUpdateIri = useUpdateIri(relationshipIri);

  const handleUpdateLabel = useUpdateLabel(relationshipIri);

  const handleUpdateDescription = useUpdateDescriptionForIri(relationshipIri);

  const handleRemoveModel = useRemoveModelForIri(relationshipIri);

  const handleAddModel = useAddModelToIri(relationshipIri);

  const handleUpdatePropertyType = useUpdatePropertyType(relationshipIri);

  const handleRemoveRelationship = useRemoveModelConceptRelationship();

  const handleAddDomain = useAddClassForRelationship({
    direction: RelationshipDirection.OUTGOING,
    relationshipIri,
  });
  const handleAddRange = useAddClassForRelationship({
    direction: RelationshipDirection.INCOMING,
    relationshipIri,
  });

  const handleRemoveDomain = useRemoveClassForRelationship({
    direction: RelationshipDirection.OUTGOING,
    relationshipIri,
  });
  const handleRemoveRange = useRemoveClassForRelationship({
    direction: RelationshipDirection.INCOMING,
    relationshipIri,
  });

  const handleClickClass = useClickModelConceptClassLink();

  const createNewClassFromQuery = useCreateModelConceptClassFromQuery();

  if (!linkData) {
    return null;
  }

  const label = labelsByIri[linkData.value];

  const descriptionForIri = descriptionsByIri[linkData.value] || '';

  return (
    <>
      <div className={Classes.DRAWER_BODY}>
        <ProjectDiagramEditableIri
          header={copy.components.projectDiagram.relationshipDrawer.name}
          iri={linkData.value}
          isHighlighted={isLabelModified}
          label={label}
          onSetIri={handleUpdateIri}
          onSetLabel={handleUpdateLabel}
          placeholder={
            label === ''
              ? copy.components.projectDiagram.modelConceptRelationship.new
              : ''
          }
          testId={TEST_IDS.projectDiagram.entityDrawer.name}
        />
        <ProjectDiagramEditableTextArea
          // key here to description changes when you change from drawer to drawer
          key={`${linkData.value}-description`}
          header={copy.components.projectDiagram.entityDrawer.description}
          isHighlighted={isDescriptionModified}
          onSetValue={handleUpdateDescription}
          value={descriptionForIri}
        />

        {allModelItems.length > 1 ? (
          <SelectableLinks
            allowEmpty={false}
            buttonText=""
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            header={copy.components.projectDiagram.entityDrawer.models}
            items={availableModels}
            itemTestId={(value, idx) =>
              getProjectDiagramEntityDrawerSelectLinkTestId(
                value,
                'models',
                idx
              )
            }
            onRemove={handleRemoveModel}
            onSelect={handleAddModel}
            removeDisabledTooltip={
              copy.components.projectDiagram.entityDrawer.modelsRemoveDisabled
            }
            selected={currentModels}
            selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
              'models'
            )}
          />
        ) : null}

        {isCustomPropertyType ? (
          <PropertyTypeSelector
            debounced={!isTesting()}
            propertyType={propertyType}
            setPropertyType={handleUpdatePropertyType}
          />
        ) : null}

        <div className="sd-drawer-project-diagram-entity-columns">
          <SelectableLinks
            buttonText=""
            creatable
            createNewItemFromQuery={createNewClassFromQuery}
            createNewItemRenderer={createNewSelectItemRenderer}
            createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
              ''
            )}
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            header={copy.components.projectDiagram.relationshipDrawer.domain}
            highlightedValues={addedSources}
            items={domainAndRangeSelectItems.availableDomains}
            itemTestId={(value, idx) =>
              getProjectDiagramEntityDrawerSelectLinkTestId(
                value,
                PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
                idx
              )
            }
            onClick={handleClickClass}
            onRemove={handleRemoveDomain}
            onSelect={handleAddDomain}
            selected={domainAndRangeSelectItems.currentDomains}
            selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
              PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE
            )}
          />
          <SelectableLinks
            buttonText=""
            creatable
            createNewItemFromQuery={createNewClassFromQuery}
            createNewItemRenderer={createNewSelectItemRenderer}
            createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
              ''
            )}
            filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
            header={copy.components.projectDiagram.relationshipDrawer.range}
            highlightedValues={addedTargets}
            items={domainAndRangeSelectItems.availableRanges}
            itemTestId={(value, idx) =>
              getProjectDiagramEntityDrawerSelectLinkTestId(
                value,
                PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE,
                idx
              )
            }
            onClick={handleClickClass}
            onRemove={handleRemoveRange}
            onSelect={handleAddRange}
            selected={domainAndRangeSelectItems.currentRanges}
            selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
            selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
              PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE
            )}
          />
        </div>

        <ProjectDiagramRelationshipDrawerTable
          domains={domainAndRangeSelectItems.currentDomains}
          highlightedDomains={addedSources}
          highlightedRanges={addedTargets}
          onClick={handleClickClass}
          ranges={domainAndRangeSelectItems.currentRanges}
        />
      </div>

      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectDiagram.entityDrawer.removeButton}
            onClick={() =>
              handleRemoveRelationship({ key: linkData.key, removeAll: true })
            }
            outlined
          >
            {copy.components.projectDiagram.entityDrawerButtons.remove}
          </Button>
        </div>
      </div>
    </>
  );
};
